import { isAxiosError } from 'axios';

import {
    AccountMini,
    BillingInfo,
    ChangeSubscriptionOptionsResponseInner,
    ChangeSubscriptionPreviewResponse,
    ChangeSubscriptionRequest,
    ChangeSubscriptionRequestAddOnsInner,
    ChangeSubscriptionRequestTimeframeEnum,
    ChangeSubscriptionResponse,
    PurchasePreview,
    PurchasePreviewRequest,
    PurchaseSubscriptionRequest,
    RenewalInvoicePreview,
    Subscription,
    SubscriptionApiChangeSubscriptionRequest,
    SubscriptionApiGetBillingInfoRequest,
    SubscriptionApiPreviewChangeSubscriptionRequest,
    SubscriptionApiGetOrgHostedLoginTokenRequest,
    SubscriptionApiPreviewPurchaseSubscriptionRequest,
    SubscriptionApiPreviewRenewalInvoiceRequest,
    SubscriptionApiUpdateBillingInfoRequest,
    SubscriptionApiUpdateSubscriptionRequest,
    SubscriptionPurchaseRecord,
    SubscriptionStatus,
    UpdateAccountInfoRequest,
    CouponRedemption,
    UserApiUpdateAccountInfoRequest,
    UserComment,
} from '@videoblocks/subscription-service-client';
import { LoggerFactory } from '@videoblocks/typescript-logger';

import { BadRequestError } from './BadRequestError';
import { PaymentError } from './PaymentError';
import { PreviewError } from './PreviewError';
import { ProcessingError } from './ProcessingError';
import { UnexpectedError } from './UnexpectedError';
import { subscriptionApi, userApi } from './subscriptionServiceConfig';

const logger = LoggerFactory.create('Subscription Api');

const getSubscriptionByUUID = async (uuid: string): Promise<Subscription | null> => {
    try {
        const response = await subscriptionApi.getSubscriptionByUuid({ uuid });
        return response.data ?? null;
    } catch (error) {
        logger.error({ error, uuid }, 'An error occurred while fetching subscription');
        return null;
    }
};

const getSubscriptionsByUserId = async (userId: number, recurlyOnly = false): Promise<Subscription[]> => {
    try {
        const response = await subscriptionApi.getSubscriptionsByUserId({ userId, recurlyOnly });
        return response.data ?? [];
    } catch (error) {
        logger.error({ error, userId }, 'An error occurred while fetching subscriptions for user');
        return [];
    }
};

const getSubscriptionsByOrgId = async (orgId: number): Promise<Subscription[]> => {
    try {
        const response = await subscriptionApi.getSubscriptionsByOrgId({ orgId });
        return response.data ?? [];
    } catch (error) {
        logger.error({ error, orgId }, 'An error occurred while fetching subscriptions for organization');
        return [];
    }
};

const getCurrentSubscriptionByUserId = async (userId: number, recurlyOnly = false): Promise<Subscription | null> => {
    try {
        const response = await subscriptionApi.getSubscriptionsByUserId({
            userId,
            recurlyOnly,
        });
        const subscriptions = response.data ?? [];

        return (
            subscriptions.find((subscription) => new Date(Date.parse(subscription.subscriptionEndDate)) > new Date()) ??
            null
        );
    } catch (error) {
        logger.error({ error, userId }, 'An error occurred while fetching subscriptions for user');
        return null;
    }
};

const getCurrentSubscriptionByOrgId = async (orgId: number): Promise<Subscription | null> => {
    try {
        const response = await subscriptionApi.getSubscriptionsByOrgId({ orgId });
        const subscriptions = response.data ?? [];

        return (
            subscriptions.find((subscription) => new Date(Date.parse(subscription.subscriptionEndDate)) > new Date()) ??
            null
        );
    } catch (error) {
        logger.error({ error, orgId }, 'An error occurred while fetching subscriptions for org');
        return null;
    }
};

const getSubscriptionsByEmail = async (email: string): Promise<Subscription[]> => {
    try {
        const response = await subscriptionApi.findSubscriptions({ email });
        return response.data ?? [];
    } catch (error) {
        logger.error({ error, email }, 'An error occurred while fetching subscriptions for email address');
        return [];
    }
};

const getHostedLoginTokenForOrg = async (orgId: number): Promise<string | null> => {
    try {
        const request: SubscriptionApiGetOrgHostedLoginTokenRequest = {
            orgId,
        };
        const response = await subscriptionApi.getOrgHostedLoginToken(request);
        return response.data.hostedLoginToken ?? null;
    } catch (error) {
        logger.error({ error, orgId }, 'An error occurred while fetching the hosted login token for an organization.');
        return null;
    }
};

const previewPurchase = async (purchasePreviewRequest: PurchasePreviewRequest): Promise<PurchasePreview> => {
    try {
        const request: SubscriptionApiPreviewPurchaseSubscriptionRequest = {
            purchasePreviewRequest,
        };
        const response = await subscriptionApi.previewPurchaseSubscription(request);
        return response.data;
    } catch (error: unknown) {
        logger.error({ purchasePreviewRequest, error }, 'Error estimating taxes');
        throw new PreviewError();
    }
};

const purchaseSubscription = async (purchaseInfo: PurchaseSubscriptionRequest): Promise<SubscriptionPurchaseRecord> => {
    try {
        const result = await subscriptionApi.createSubscription({
            createSubscriptionRequest: purchaseInfo,
        });

        return result.data as SubscriptionPurchaseRecord;
    } catch (error: unknown) {
        if (isAxiosError(error)) {
            if (error.response?.status === 402) {
                throw new PaymentError();
            }
            // Other specific error types can be processed here.
        }
        throw new ProcessingError();
    }
};

const getComments = async (userId: number): Promise<UserComment[]> => {
    try {
        const request = { userId };
        const result = await userApi.getUserComments(request);
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 404) {
                logger.warn({ error, userId }, 'Account comments not found.');
                return [];
            }
        }
        logger.error({ error, userId }, 'An error occurred while retrieving account comments.');
        throw new UnexpectedError();
    }
};

const updateAccountInfo = async (
    userId: number,
    updateRequest: UpdateAccountInfoRequest,
): Promise<AccountMini | null> => {
    try {
        const request: UserApiUpdateAccountInfoRequest = {
            userId,
            updateAccountInfoRequest: updateRequest,
        };
        const result = await userApi.updateAccountInfo(request);
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.warn({ error, userId }, 'Invalid account info update request');
                throw new BadRequestError();
            } else if (error.response?.status === 404) {
                logger.warn({ error, userId }, 'Account info not found');
                return null;
            } else if (error.response?.status === 402) {
                throw new PaymentError();
            }
        }
        logger.error({ error, userId }, 'An error occurred while updating Account info');
        throw new UnexpectedError();
    }
};

const getBillingInfo = async (userId: number): Promise<BillingInfo | null> => {
    try {
        const request: SubscriptionApiGetBillingInfoRequest = { userId };
        const result = await subscriptionApi.getBillingInfo(request);
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 404) {
                logger.warn({ error, userId }, 'Billing info not found.');
                return null;
            }
        }
        logger.error({ error, userId }, 'An error occurred while retrieving Billing info.');
        throw new UnexpectedError();
    }
};

const updateBillingInfo = async (userId: number, tokenId: string): Promise<BillingInfo | null> => {
    try {
        const request: SubscriptionApiUpdateBillingInfoRequest = {
            userId,
            updateBillingInfoRequest: {
                tokenId,
            },
        };
        const result = await subscriptionApi.updateBillingInfo(request);
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.warn({ error, userId }, 'Invalid billing info update request');
                throw new BadRequestError();
            } else if (error.response?.status === 404) {
                logger.warn({ error, userId }, 'Billing info not found');
                return null;
            } else if (error.response?.status === 402) {
                throw new PaymentError();
            }
        }
        logger.error({ error, userId }, 'An error occurred while updating billing info');
        throw new UnexpectedError();
    }
};

const cancelSubscription = async (uuid: string, reasonCode: string): Promise<Subscription | null> => {
    const request: SubscriptionApiUpdateSubscriptionRequest = {
        uuid,
        updateSubscriptionRequest: {
            status: SubscriptionStatus.Canceled,
            reasonCode,
        },
    };
    try {
        const response = await subscriptionApi.updateSubscription(request);
        return response.data;
    } catch (error) {
        logger.error({ uuid, reasonCode, error }, `An error occurred while canceling subscription with uuid %s`, uuid);
    }
    return null;
};

const restartSubscription = async (
    uuid: string,
    planCode: string,
    timeframe: string = 'now',
): Promise<Subscription | null> => {
    const request: SubscriptionApiUpdateSubscriptionRequest = {
        uuid,
        updateSubscriptionRequest: {
            status: SubscriptionStatus.Active,
            planCode,
            timeframe: timeframe as ChangeSubscriptionRequestTimeframeEnum,
        },
    };
    try {
        const response = await subscriptionApi.updateSubscription(request);
        return response.data;
    } catch (error) {
        logger.error(
            { uuid, planCode, timeframe, error },
            `An error occurred while restarting subscription with uuid %s`,
            uuid,
        );
    }
    return null;
};

const changeSubscription = async (
    uuid: string,
    planCode: string,
    addOns?: Array<ChangeSubscriptionRequestAddOnsInner> | null,
    timeframe?: string,
): Promise<ChangeSubscriptionResponse | null> => {
    const changeRequest: ChangeSubscriptionRequest = {
        planCode,
        ...(addOns && { addOns }),
        ...(timeframe && { timeframe: timeframe as ChangeSubscriptionRequestTimeframeEnum }),
    };

    const request: SubscriptionApiChangeSubscriptionRequest = {
        uuid,
        changeSubscriptionRequest: changeRequest,
    };

    try {
        const response = await subscriptionApi.changeSubscription(request);
        return response.data;
    } catch (error) {
        logger.error({ error, uuid, planCode, timeframe }, 'An error occurred while changing subscription');
        return null;
    }
};

const previewChangeSubscription = async (
    uuid: string,
    planCode: string,
    addOn?: string,
    timeframe?: string,
): Promise<ChangeSubscriptionPreviewResponse | null> => {
    const request: SubscriptionApiPreviewChangeSubscriptionRequest = {
        uuid,
        planCode,
        addOn,
        ...(timeframe && { timeframe: timeframe as ChangeSubscriptionRequestTimeframeEnum }),
    };

    const response = await subscriptionApi.previewChangeSubscription(request);
    return response.data;
};

const getChangeSubscriptionOptions = async (uuid: string): Promise<ChangeSubscriptionOptionsResponseInner[] | null> => {
    try {
        const response = await subscriptionApi.getChangeSubscriptionOptions({ uuid });
        return response.data ?? null;
    } catch (error) {
        logger.error({ error, uuid }, 'An error occurred while fetching change subscription options');
        return null;
    }
};

const redeemCoupon = async (uuid: string, couponCode: string): Promise<CouponRedemption> => {
    const response = await subscriptionApi.redeemCoupon({
        uuid,
        redeemCouponRequest: { couponCode },
    });
    return response.data;
};

const listCouponRedemptions = async (uuid: string): Promise<CouponRedemption[]> => {
    const response = await subscriptionApi.listSubscriptionCouponRedemptions({ uuid });
    return response.data;
};

const listAccountCouponRedemptions = async (userId: number): Promise<CouponRedemption[]> => {
    const response = await userApi.listAccountCouponRedemptions({ userId });
    return response.data;
};

const previewRenewalInvoice = async (uuid: string): Promise<RenewalInvoicePreview | null> => {
    try {
        const request: SubscriptionApiPreviewRenewalInvoiceRequest = { uuid };
        const response = await subscriptionApi.previewRenewalInvoice(request);
        return response.data ?? null;
    } catch (error) {
        logger.error({ error, uuid }, 'An error occurred while fetching renewal invoice preview');
        return null;
    }
};

export const SubscriptionsApi = {
    getSubscriptionByUUID,
    getSubscriptionsByUserId,
    getSubscriptionsByOrgId,
    getSubscriptionsByEmail,
    getCurrentSubscriptionByUserId,
    getCurrentSubscriptionByOrgId,
    getHostedLoginTokenForOrg,
    previewPurchase,
    purchaseSubscription,
    getComments,
    updateAccountInfo,
    getBillingInfo,
    updateBillingInfo,
    cancelSubscription,
    restartSubscription,
    changeSubscription,
    previewChangeSubscription,
    getChangeSubscriptionOptions,
    redeemCoupon,
    listCouponRedemptions,
    listAccountCouponRedemptions,
    previewRenewalInvoice,
};
